import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge, currency } from "@/components/Primitives";
import { ArrowLeft, TrendingUp, FileSignature, Megaphone, Globe } from "lucide-react";

export default function PersonProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [nf, setNf] = useState(false);

  useEffect(() => { api.get(`/people/${id}/activity`).then((r) => setData(r.data)).catch(() => setNf(true)); }, [id]);
  if (nf) return <div className="text-[#555]">Person not found.</div>;
  if (!data) return null;
  const p = data.person;
  const total = data.deals.length + data.transactions.length + data.listings.length + data.profiles.length;

  const Section = ({ icon: Icon, title, count, children }) => (
    <div className="bg-white border border-[#E2E6F0] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E2E6F0] flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#F7941D]" strokeWidth={1.5} /><h3 className="font-serif text-xl">{title}</h3>
        <span className="ml-auto text-sm text-[#999]">{count}</span>
      </div>
      <div className="p-4">{count ? children : <div className="text-sm text-[#999] px-2 py-3">None linked.</div>}</div>
    </div>
  );
  const Row = ({ left, sub, right }) => (
    <div className="flex items-center justify-between px-2 py-2.5 border-b border-[#F0F2F8] last:border-0">
      <div className="min-w-0"><div className="text-sm font-medium text-[#111] truncate">{left}</div>{sub && <div className="text-xs text-[#777]">{sub}</div>}</div>
      {right && <div className="text-sm text-[#555] shrink-0 ml-3">{right}</div>}
    </div>
  );

  return (
    <div data-testid="person-profile-page">
      <button data-testid="back-to-people" onClick={() => navigate("/people")} className="inline-flex items-center gap-2 text-sm text-[#555] hover:text-[#1F3C88] mb-6"><ArrowLeft className="w-4 h-4" /> All people</button>
      <div className="flex items-center gap-4 mb-8">
        {p.headshot ? <img src={p.headshot} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-[#EAF0FB]" />}
        <div>
          <h1 className="font-serif text-4xl tracking-tight text-[#111]">{p.name}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-[#555]"><StatusBadge tone="green">{p.type}</StatusBadge>{p.company}{p.email && <span>· {p.email}</span>}{p.phone && <span>· {p.phone}</span>}</div>
          <div className="text-xs text-[#999] mt-1">{total} linked record{total === 1 ? "" : "s"} across your tools</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section icon={TrendingUp} title="Sales Deals" count={data.deals.length}>
          {data.deals.map((d) => <Row key={d.id} left={`${d.address || d.property_address || "Deal"} · ${d.first_name || ""} ${d.last_name || ""}`.trim()} sub={d.status} right={currency(d.contract_value)} />)}
        </Section>
        <Section icon={FileSignature} title="Transactions" count={data.transactions.length}>
          {data.transactions.map((t) => <Row key={t.transaction_id} left={t.property_address} sub={`${t.status} · closes ${t.closing_date || "TBD"}`} right={t.purchase_price ? currency(t.purchase_price) : ""} />)}
        </Section>
        <Section icon={Megaphone} title="Listings" count={data.listings.length}>
          {data.listings.map((l) => <Row key={l.id} left={l.address_line} sub={`${l.city} · ${l.status}`} right={currency(l.price)} />)}
        </Section>
        <Section icon={Globe} title="Landing Pages" count={data.profiles.length}>
          {data.profiles.map((pr) => <Row key={pr.username} left={pr.display_name} sub={`/a/${pr.username}`} right={`${pr.views || 0} views`} />)}
        </Section>
      </div>
    </div>
  );
}
